import { useMemo } from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CalendarClock } from "lucide-react";

/**
 * India Fiscal Year picker (Apr-Mar).
 *
 * Props:
 *  value         — "all" | "2025" | "2026" ...   (FY-start year as string, or "all")
 *  onChange(v)   — fires with new value
 *  onRangeChange(from, to) — optional callback, receives ISO from/to for the FY
 *  size          — "sm" | "md"  (default "md")
 *  years         — array of FY-start years to show. defaults to current FY ± 4
 *  label         — optional label prefix (default "FY")
 */
export default function FYPicker({ value = "all", onChange, onRangeChange, size = "md", years, label = "FY" }) {
  const options = useMemo(() => {
    if (years) return years;
    const now = new Date();
    const currentStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    const out = [];
    for (let y = currentStart + 1; y >= currentStart - 4; y--) out.push(y);
    return out;
  }, [years]);

  const handle = (v) => {
    onChange?.(v);
    if (onRangeChange) {
      if (v === "all") onRangeChange("", "");
      else {
        const y = Number(v);
        onRangeChange(`${y}-04-01`, `${y + 1}-03-31`);
      }
    }
  };

  const cls = size === "sm" ? "h-8 text-[12px]" : "h-9 text-[13px]";

  return (
    <Select value={value} onValueChange={handle}>
      <SelectTrigger className={`${cls} w-[140px] gap-1.5`} data-testid="fy-picker">
        <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All time</SelectItem>
        {options.map((y) => (
          <SelectItem key={y} value={String(y)}>
            {label} {y}-{String(y + 1).slice(-2)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Utility: convert FY value → ISO date range */
export function fyToRange(value) {
  if (!value || value === "all") return { from: "", to: "" };
  const y = Number(value);
  return { from: `${y}-04-01`, to: `${y + 1}-03-31` };
}

/** Utility: label for a FY value */
export function fyLabel(value) {
  if (!value || value === "all") return "All time";
  const y = Number(value);
  return `FY ${y}-${String(y + 1).slice(-2)}`;
}

/** Utility: pick current FY start year as string */
export function currentFYStart() {
  const now = new Date();
  return String(now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1);
}
